import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { Container, Button, Alert, Spinner, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { storageRoomService, StorageRoom } from '../services/storageRoom.service';
import { isSampleLocalMode } from '../services/systems/sample-local/sampleLocalMode';
import { formatSampleDateTime } from '../utils/dateFnsLocale';
import StorageRoomFormModal from '../components/storageRooms/StorageRoomFormModal';
import StorageRoomDeleteModal from '../components/storageRooms/StorageRoomDeleteModal';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { useMdUp } from '../hooks/useLaboSukeMdUp';
import { canManageStorageRoom } from '../utils/samplePermissions';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import SampleListChromeTools, {
  ToolsMenuColVis,
  ToolsMenuExport,
  ToolsMenuHelp,
} from '../components/samples/SampleListChromeTools';
import { shouldRebuildDataTable, DataTableConfig, destroyDataTableIfPresent } from '../utils/dataTableLifecycle';
import {
  buildDataTablesExportButtons,
  buildDataTablesLanguage,
  syncDataTablesPaginateVisibility,
} from '../utils/jqueryDatatablesShim';
import { translateAxiosErrorMessage } from '../utils/error.utils';
import { escapeHtml } from '../utils/escapeHtml';
import {
  formatEntityDisplayName,
  softBreakIdentifier,
} from '../utils/softBreakIdentifier';
import { logger, toLoggableError } from '../utils/logger';
import {
  buildStorageRoomCardFacts,
  formatStorageRoomOpenCaseLabels,
  formatStorageRoomTypeDisplay,
} from '../utils/storageRoomTypeLabels';
import { formatCaseTreeLocationDisplay } from '../utils/caseLocationUtils';
import {
  dataTablesLengthA11yLabels,
  dockStorageRoomsChrome,
  syncDataTablesLengthA11y,
} from '../utils/sampleListExportDock';
import { remPx, syncWeightedTableColgroup } from '../utils/syncDataTableColgroup';
import { navigateToCaseManagerWithCell } from '../utils/samplePageNavigation';
import { injectDataTableCellSeparators } from '../utils/injectDataTableCellSeparators';
import SampleExternalShareModal from '../components/samples/SampleExternalShareModal';
import type { LocationShareTarget } from '../utils/locationShare';
import {
  SAMPLE_STORAGE_SETUP_MODAL_SESSION_KEY,
  SAMPLE_STORAGE_SETUP_QUERY_KEY,
  isStorageSetupSearchParams,
} from '../utils/sampleStorageSetupGate';
// Note: DataTables CSS is loaded from local files in index.html (/vendor/datatables/css/)

type StorageRoomTableRow = StorageRoom & { _ownerName?: string };

/** table-layout:fixed の余りが操作列へ流れるのを防ぎ、保管庫名へ配分する */
function syncStorageRoomsColgroup(table: HTMLTableElement | null): void {
  /* 3×touch + gap（共有・編集・削除）。ケース管理は保管庫名リンクが正 */
  const actionsW = Math.round(3 * remPx(2.75) + remPx(1));
  syncWeightedTableColgroup(table, (th) => {
    if (th.classList.contains('sr-actions-col')) {
      return { role: 'fixed', widthPx: actionsW };
    }
    if (th.classList.contains('sr-shared-col')) return { role: 'fixed', widthPx: remPx(7) };
    /* 「保管タイプ」「超低温冷凍庫」が欠けるのを防ぐ（旧 5.5→9 でも狭い環境あり） */
    if (th.classList.contains('sr-type-col')) return { role: 'fixed', widthPx: remPx(11) };
    if (th.classList.contains('sr-created-col')) return { role: 'fixed', widthPx: remPx(8) };
    if (th.classList.contains('sr-hide-mobile')) return { role: 'fixed', widthPx: remPx(7.5) };
    /* 「システム 管理者」が1行で収まる幅（旧 5.5rem は中途省略が早すぎた） */
    if (th.classList.contains('sr-owner-col')) return { role: 'fixed', widthPx: remPx(9.5) };
    if (th.classList.contains('sr-name-col')) return { role: 'fluid', weight: 0.68 };
    if (th.classList.contains('sr-location-col')) return { role: 'fluid', weight: 0.32 };
    return { role: 'fixed', widthPx: remPx(5) };
  });
}

/** xs カード1段化: 名前行 + タイプ·場所·所有者 facts（タイプ列は md+） */
function renderStorageRoomNameCell(
  data: unknown,
  row: StorageRoomTableRow,
  t: TFunction<'translation', undefined>
): string {
  const sharedBadge = row.isShared
    ? `<span class="badge bg-info text-dark sr-shared-badge">${t('storage_room.is_shared_public')}</span>`
    : '';
  const typeRaw = row.type == null || row.type === '' ? '' : String(row.type);
  const typeDisp = formatStorageRoomTypeDisplay(typeRaw, t);
  const rawName = data == null || data === '' ? '' : String(data);
  const displayName = rawName ? formatEntityDisplayName(rawName, t) : '-';
  /* タイプは名前横バッジではなく facts へ（xs で名前幅を返す）。冗長タイプ・長い組織名は畳む */
  const sampleCount = Array.isArray(row.samples) ? row.samples.length : null;
  const sampleCountLabel =
    sampleCount != null
      ? t('storage_room.sample_count', {
          count: sampleCount,
          defaultValue: `${sampleCount}件`,
        })
      : null;
  const facts = buildStorageRoomCardFacts({
    roomName: displayName === '-' ? '' : displayName,
    typeDisplay: typeDisp,
    location: String(row.location ?? '').trim(),
    owner: String(row._ownerName ?? '').trim(),
    sampleCountLabel,
  });
  // 名前と facts の textContent 境界用（可視は改行。先頭 ` · ` を facts に載せない）
  const nameFactsSep = facts.display
    ? `<span class="visually-hidden" aria-hidden="true"> · </span>`
    : '';
  const factsHtml = facts.display
    ? `<div class="sr-mobile-facts" title="${escapeHtml(facts.title)}">${escapeHtml(
        softBreakIdentifier(facts.display)
      )}</div>`
    : '';
  const softName = escapeHtml(softBreakIdentifier(displayName)) || '-';
  const openCasesFull = t('sample.case_manager.open_in_case_manager', {
    defaultValue: 'Open in Cases',
  });
  const openCasesShort = t('sample.case_manager.open_in_case_manager_short', {
    defaultValue: openCasesFull,
  });
  const openLabels = formatStorageRoomOpenCaseLabels(
    displayName,
    String(row._ownerName ?? ''),
    { short: openCasesShort, full: openCasesFull }
  );
  const openAria = escapeHtml(openLabels.aria);
  const openTitle = escapeHtml(openLabels.title);
  return `<div class="sr-name-stack"><div class="sr-name-main"><button type="button" class="btn btn-link p-0 align-baseline text-start sr-open-cases-btn sr-name-text sample-ellipsis-prefer-end" data-id="${row.id}" title="${openTitle}" aria-label="${openAria}">${softName}</button>${sharedBadge}</div>${nameFactsSep}${factsHtml}</div>`;
}

export default function StorageRoomsManager() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLocationShareModal, setShowLocationShareModal] = useState(false);
  const [locationShareTarget, setLocationShareTarget] = useState<LocationShareTarget | null>(null);
  const [selectedStorageRoom, setSelectedStorageRoom] = useState<StorageRoom | null>(null);
  const [showSetupNextStep, setShowSetupNextStep] = useState(false);
  const setupMode = isStorageSetupSearchParams(searchParams);
  const setupModeRef = useRef(setupMode);
  setupModeRef.current = setupMode;
  const tableRef = useRef<HTMLTableElement>(null);
  const dataTableRef = useRef<any>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(isAdmin);
  const isMdUp = useMdUp();

  /** セットアップ導線: 新規モーダルを同一タブで1回だけ自動オープン */
  useEffect(() => {
    if (!setupMode) return;
    try {
      if (sessionStorage.getItem(SAMPLE_STORAGE_SETUP_MODAL_SESSION_KEY) === '1') return;
      sessionStorage.setItem(SAMPLE_STORAGE_SETUP_MODAL_SESSION_KEY, '1');
    } catch {
      /* private mode 等でもモーダルは開く */
    }
    setShowCreateModal(true);
  }, [setupMode]);

  const clearSetupQuery = useCallback(() => {
    if (!searchParams.has(SAMPLE_STORAGE_SETUP_QUERY_KEY)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(SAMPLE_STORAGE_SETUP_QUERY_KEY);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openRoomInCaseManager = useCallback(
    (roomId: number) => {
      navigateToCaseManagerWithCell(navigate, {
        storageRoomId: roomId,
        drawerNumber: null,
        caseName: null,
      });
    },
    [navigate]
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['storageRooms', adminViewAll],
    queryFn: () =>
      storageRoomService.list({
        page: 1,
        limit: 1000,
        adminViewAll: adminViewAll || undefined,
      }),
  });

  const roomTotal = data?.pagination?.total ?? data?.data?.length ?? 0;
  const roomTotalRef = useRef(roomTotal);
  roomTotalRef.current = roomTotal;
  const zeroRooms = !isLoading && !isError && data != null && roomTotal === 0;
  const showSetupBanner = setupMode || zeroRooms;

  const createMutation = useMutation({
    mutationFn: (data: { name: string; location?: string | null; type?: string | null; isShared?: boolean }) =>
      storageRoomService.create(data),
    onSuccess: () => {
      const offerNextStep = setupModeRef.current || roomTotalRef.current === 0;
      queryClient.invalidateQueries({ queryKey: ['storageRooms'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setShowCreateModal(false);
      setError('');
      if (offerNextStep) {
        setShowSetupNextStep(true);
        clearSetupQuery();
        try {
          sessionStorage.removeItem(SAMPLE_STORAGE_SETUP_MODAL_SESSION_KEY);
        } catch {
          /* ignore */
        }
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { name?: string; location?: string | null; type?: string | null; isShared?: boolean };
    }) => storageRoomService.update(id, data, { adminViewAll: adminViewAll || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storageRooms'] });
      setShowEditModal(false);
      setSelectedStorageRoom(null);
      setError('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      storageRoomService.delete(id, { adminViewAll: adminViewAll || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storageRooms'] });
      setShowDeleteModal(false);
      setSelectedStorageRoom(null);
      setError('');
    },
    onError: (err: unknown) => {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('storage_room.delete_failed', { defaultValue: 'Failed to delete storage room.' })
        )
      );
    },
  });

  const handleCreate = async (data: { name?: string; location?: string | null; type?: string | null; isShared?: boolean }) => {
    if (!data.name) return;
    setError('');
    await createMutation.mutateAsync({
      name: data.name,
      location: data.location || null,
      type: data.type || null,
      isShared: data.isShared ?? false,
    });
  };

  const handleEdit = useCallback(async (storageRoom: StorageRoom) => {
    try {
      const { data: detail } = await storageRoomService.getById(storageRoom.id);
      setSelectedStorageRoom(detail);
      setShowEditModal(true);
    } catch (err) {
      setError(
        translateAxiosErrorMessage(
          err,
          t,
          t('storage_room.load_failed', { defaultValue: 'Failed to load storage rooms.' })
        )
      );
    }
  }, [t]);

  const handleUpdate = async (data: { name?: string; location?: string | null; type?: string | null; isShared?: boolean }) => {
    if (!selectedStorageRoom) return;
    setError('');
    await updateMutation.mutateAsync({ id: selectedStorageRoom.id, data });
  };

  const handleDelete = useCallback((storageRoom: StorageRoom) => {
    setSelectedStorageRoom(storageRoom);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = async () => {
    if (selectedStorageRoom) {
      await deleteMutation.mutateAsync(selectedStorageRoom.id);
    }
  };

  const hasData = Array.isArray(data?.data);
  const previousConfigRef = useRef<DataTableConfig | null>(null);

  const mapStorageRoomsWithOwnerName = useCallback((rows: StorageRoom[]): StorageRoomTableRow[] => {
    return rows.map((row) => {
      const u = row.user;
      const last = (u?.lastName || '').trim();
      const first = (u?.firstName || '').trim();
      const ownerName = [last, first].filter(Boolean).join(' ') || '-';
      return { ...row, _ownerName: ownerName };
    });
  }, []);

  const destroyTable = useCallback(() => {
    const currentElement = tableRef.current;
    if (currentElement && typeof window !== 'undefined' && (window as any).jQuery) {
      const $ = (window as any).jQuery;
      $(currentElement).off('click', '.edit-btn');
      $(currentElement).off('click', '.delete-btn');
      $(currentElement).off('click', '.sr-open-cases-btn');
      $(currentElement).off('click', '.share-btn');
    }
    destroyDataTableIfPresent(currentElement);
    dataTableRef.current = null;
    previousConfigRef.current = null;
  }, []);

  useLayoutEffect(() => {
    if (!hasData || !data?.data || !Array.isArray(data.data)) {
      if (dataTableRef.current) {
        destroyTable();
      }
      return;
    }

    const tableElement = tableRef.current;
    if (!tableElement) {
      if (dataTableRef.current) {
        destroyTable();
      }
      return;
    }

    const prev = previousConfigRef.current;
    const nextConfig: DataTableConfig = {
      language: i18n.language,
      isAdmin,
      hasData,
      adminViewAll,
    };

    if (typeof window !== 'undefined' && (window as any).jQuery) {
      const $ = (window as any).jQuery;
      if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableElement)) {
        if (!shouldRebuildDataTable(prev, nextConfig)) {
          if (dataTableRef.current && Array.isArray(data?.data)) {
            try {
              dataTableRef.current.clear();
              dataTableRef.current.rows.add(mapStorageRoomsWithOwnerName(data.data));
              dataTableRef.current.draw();
            } catch (err) {
              logger.error('DataTables更新エラー', toLoggableError(err));
            }
          }
          return;
        }
        destroyTable();
      }
    }

    try {
      if (typeof window === 'undefined' || !(window as any).jQuery || !(window as any).jQuery.fn.DataTable) {
        setError(t('common.datatables_not_loaded', { defaultValue: 'DataTables is not loaded. Please load jQuery and DataTables.' }));
        return;
      }

      const $ = (window as any).jQuery;
      
      // search ラベル「検索:」は空にし、プレースホルダ／aria-label／title で用途を示す
      const searchPlaceholder = t('storage_room.search_placeholder');
      // placeholder は短、aria は画面固有、title は補足
      const searchAria = t('storage_room.search_aria', {
        defaultValue: '保管庫検索',
      });
      const searchTitle = t('storage_room.search_title', {
        defaultValue: '保管庫名・タイプ・所有者で絞り込み',
      });
      const dataTablesLanguage = { ...buildDataTablesLanguage(t), search: '' };
      
      if (!data?.data || !Array.isArray(data.data)) {
        return;
      }

      const tableData = mapStorageRoomsWithOwnerName(data.data);

      destroyDataTableIfPresent(tableElement);

      const dt = $(tableElement).DataTable({
        data: tableData,
        columns: [
          {
            data: '_ownerName',
            title: t('storage_room.owner_name', { defaultValue: '所有者' }),
            className: 'sr-owner-col',
            defaultContent: '-',
            /** 自分の保管庫だけ見ているときは全行同一で冗長（管理者の全件表示時のみ） */
            visible: isAdmin && adminViewAll,
            render: (data: unknown) => {
              const text = escapeHtml(data ?? '-') || '-';
              return text === '-'
                ? '-'
                : `<span class="sr-cell-ellipsis" title="${text}">${text}</span>`;
            },
          },
          {
            data: 'location',
            title: t('storage_room.location', { defaultValue: 'Location' }),
            className: 'sr-location-col',
            defaultContent: '-',
            render: (data: unknown) => {
              const raw = data == null || data === '' ? '' : String(data);
              if (!raw || raw === '-') return '-';
              const loc = formatCaseTreeLocationDisplay(raw);
              const text = escapeHtml(loc.full) || '-';
              const soft = escapeHtml(softBreakIdentifier(loc.display)) || '-';
              return `<span class="sr-cell-ellipsis" title="${text}">${soft}</span>`;
            },
          },
          {
            data: 'name',
            title: t('storage_room.storage_name'),
            className: 'sr-name-col',
            render: (data: unknown, _type: string, row: StorageRoomTableRow) =>
              renderStorageRoomNameCell(data, row, t),
          },
          {
            data: 'type',
            title: t('storage_room.storage_type'),
            className: 'sr-type-col',
            defaultContent: '-',
            render: (data: unknown) => {
              const raw = data == null || data === '' ? '' : String(data);
              const display = formatStorageRoomTypeDisplay(raw, t);
              const text = escapeHtml(display);
              const soft = escapeHtml(softBreakIdentifier(display));
              return text === '-' || text === ''
                ? '-'
                : `<span class="sr-cell-ellipsis" title="${text}">${soft || text}</span>`;
            },
          },
          {
            data: 'isShared',
            title: t('storage_room.is_shared_column'),
            className: 'sr-shared-col',
            defaultContent: '-',
            render: (data: boolean | undefined) =>
              data
                ? `<span class="badge bg-info text-dark">${t('storage_room.is_shared_public')}</span>`
                : `<span class="badge bg-secondary">${t('storage_room.is_shared_private')}</span>`,
          },
          {
            data: 'createdAt',
            title: t('common.created_at'),
            className: 'sr-hide-mobile sr-created-col',
            /** 主作業列を優先。ColVis で再表示可 */
            visible: false,
            render: (data: any) => {
              if (!data) return '-';
              try {
                return formatSampleDateTime(data, i18n.language);
              } catch {
                return data;
              }
            },
          },
          {
            data: null,
            title: t('common.actions'),
            className: 'text-center sr-actions-col',
            orderable: false,
            defaultContent: '',
            render: (_data: any, _type: any, row: StorageRoomTableRow) => {
              const canEdit = canManageStorageRoom(row, user?.id, { isAdmin, adminViewAll });
              if (!canEdit) return '-';
              const hasSamples = Array.isArray(row.samples) && row.samples.length > 0;
              const displayName = formatEntityDisplayName(row.name || String(row.id), t);
              const ownerName = String(row._ownerName ?? '');
              const shareLabels = formatStorageRoomOpenCaseLabels(displayName, ownerName, {
                short: t('sample.external_share_links_short', { defaultValue: '共有' }),
                full: t('sample.location_share_room', { defaultValue: 'この保管庫を共有' }),
              });
              const editLabels = formatStorageRoomOpenCaseLabels(displayName, ownerName, {
                short: t('common.edit'),
                full: t('storage_room.edit_room_description'),
              });
              const deleteBlockedTitle = t('storage_room.cannot_delete_has_samples');
              const deleteBlockedShort = t('storage_room.cannot_delete_has_samples_short');
              const deleteLabels = hasSamples
                ? formatStorageRoomOpenCaseLabels(displayName, ownerName, {
                    short: deleteBlockedShort,
                    full: deleteBlockedTitle,
                  })
                : formatStorageRoomOpenCaseLabels(displayName, ownerName, {
                    short: t('common.delete'),
                    full: t('storage_room.delete_room_description'),
                  });
              const deleteClass = hasSamples ? 'delete-btn disabled-action' : 'delete-btn';
              const deleteStyle = hasSamples ? 'opacity: 0.5; cursor: not-allowed;' : '';
              const shareBtn = isSampleLocalMode()
                ? ''
                : `<button type="button" class="btn border-0 bg-transparent text-secondary share-btn sr-icon-btn" data-id="${row.id}" title="${escapeHtml(shareLabels.title)}" aria-label="${escapeHtml(shareLabels.aria)}">
                    <i class="fas fa-share-alt" aria-hidden="true"></i>
                  </button>`;
              return `
                <div class="btn-group" role="group">
                  ${shareBtn}
                  <button type="button" class="btn border-0 bg-transparent text-primary edit-btn sr-icon-btn" data-id="${row.id}" title="${escapeHtml(editLabels.title)}" aria-label="${escapeHtml(editLabels.aria)}">
                    <i class="fas fa-pen" aria-hidden="true"></i>
                  </button>
                  <button type="button" class="btn border-0 bg-transparent text-danger sr-icon-btn ${deleteClass}" data-id="${row.id}" title="${escapeHtml(deleteLabels.title)}" aria-label="${escapeHtml(deleteLabels.aria)}" ${hasSamples ? 'disabled aria-disabled="true"' : ''} style="${deleteStyle}">
                    <i class="fas fa-trash-alt" aria-hidden="true"></i>
                  </button>
                </div>
              `;
            },
          },
        ],
        language: dataTablesLanguage,
        pageLength: 25,
        lengthMenu: [10, 25, 50, 100],
        order: [[2, 'asc']],
        dom: 'lBfrtip',
        buttons: buildDataTablesExportButtons({
          t,
          asCollection: true,
          colvisOutsideCollection: true,
          filename: () => `storage-rooms_${format(new Date(), 'yyyy-MM-dd')}`,
          excelTitle: t('navigation.storage_room_management', {
            defaultValue: 'Storage rooms',
          }),
        }),
        responsive: true,
        autoWidth: false,
        createdRow: (row: HTMLElement) => {
          injectDataTableCellSeparators(row);
        },
        drawCallback: function (this: {
          api: () => Parameters<typeof syncDataTablesPaginateVisibility>[0];
        }) {
          syncDataTablesPaginateVisibility(this.api());
        },
        initComplete: function (this: {
          api: () => {
            row: (n: JQuery) => { data: () => unknown };
          } & Parameters<typeof syncDataTablesPaginateVisibility>[0];
        }) {
          // initComplete は `const dt = …DataTable()` 返却前に走るため、dt を触らず this.api() を使う
          const api = this.api();
          syncDataTablesPaginateVisibility(api);
          dockStorageRoomsChrome(dataTablesLengthA11yLabels(t));
          syncStorageRoomsColgroup(tableElement);
          syncDataTablesLengthA11y('#storage-rooms-table-wrapper .dataTables_wrapper', {
            ...dataTablesLengthA11yLabels(t),
            lengthSlotSelector: '#storage-rooms-page .dataTables-length-slot',
          });
          const $filterInput = $('#storage-rooms-page .dataTables-filter-slot input[type="search"]');
          ($filterInput.length
            ? $filterInput
            : $(tableElement).closest('.dataTables_wrapper').find('.dataTables_filter input[type="search"]')
          ).attr({
            placeholder: searchPlaceholder,
            'aria-label': searchAria,
            title: searchTitle,
          });

          const openCasesFromRow = (el: HTMLElement) => {
            const rowData = api.row($(el).closest('tr')).data() as StorageRoom | undefined;
            if (rowData?.id != null) openRoomInCaseManager(rowData.id);
          };

          $(tableElement)
            .off('click', '.sr-open-cases-btn')
            .on('click', '.sr-open-cases-btn', function (this: HTMLElement, e: JQuery.Event) {
              e.preventDefault();
              openCasesFromRow(this);
            });

          $(tableElement).off('click', '.share-btn').on('click', '.share-btn', function (this: HTMLElement, e: JQuery.Event) {
            e.preventDefault();
            e.stopPropagation();
            const rowData = api.row($(this).closest('tr')).data() as StorageRoom | undefined;
            if (rowData?.id != null) {
              setLocationShareTarget({ scope: 'room', storageRoomId: rowData.id });
              setShowLocationShareModal(true);
            }
          });

          $(tableElement).off('click', '.edit-btn').on('click', '.edit-btn', function (this: HTMLElement, e: JQuery.Event) {
            e.preventDefault();
            const rowData = api.row($(this).closest('tr')).data() as StorageRoom | undefined;
            if (rowData) {
              handleEdit(rowData);
            }
          });

          $(tableElement).off('click', '.delete-btn').on('click', '.delete-btn', function (this: HTMLElement, e: JQuery.Event) {
            e.preventDefault();
            e.stopPropagation();
            const $btn = $(this);
            const rowData = api.row($(this).closest('tr')).data() as StorageRoom | undefined;

            if (!rowData) {
              return;
            }

            const hasSamples = Array.isArray(rowData.samples) && rowData.samples.length > 0;

            if ($btn.hasClass('disabled-action') || hasSamples) {
              if (hasSamples) {
                setError(
                  t('storage_room.cannot_delete_has_samples', {
                    defaultValue: 'Cannot delete: samples are linked to this room.',
                  })
                );
              }
              return;
            }

            handleDelete(rowData);
          });
        },
      });

      dataTableRef.current = dt;
      previousConfigRef.current = {
        language: i18n.language,
        isAdmin,
        hasData,
        adminViewAll,
      };
    } catch (err) {
      logger.error('DataTables初期化エラー', toLoggableError(err));
      setError(t('common.table_init_failed', { defaultValue: 'Failed to initialize table' }));
    }

    return () => {
      // クリーンアップ時は destroyTable を呼ばない（React StrictMode での二重破棄を防ぐ）
      // 代わりに、次のマウント時に destroyTable が呼ばれる
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasData,
    isAdmin,
    adminViewAll,
    i18n.language,
    t,
    handleEdit,
    handleDelete,
    mapStorageRoomsWithOwnerName,
    openRoomInCaseManager,
  ]);

  useLayoutEffect(() => {
    const lengthA11y = dataTablesLengthA11yLabels(t);
    dockStorageRoomsChrome(lengthA11y);
    syncStorageRoomsColgroup(tableRef.current);
    syncDataTablesLengthA11y('#storage-rooms-table-wrapper .dataTables_wrapper', {
      ...lengthA11y,
      lengthSlotSelector: '#storage-rooms-page .dataTables-length-slot',
    });
  }, [hasData, i18n.language, data?.data, t]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dockStorageRoomsChrome(dataTablesLengthA11yLabels(t));
        syncStorageRoomsColgroup(tableRef.current);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timer);
    };
  }, [t]);

  useEffect(() => {
    // useLayoutEffect で初期化された DataTables インスタンスが存在し、
    // かつ再初期化が不要な場合のみデータを更新
    if (!dataTableRef.current || !Array.isArray(data?.data)) return;
    
    // DataTables が既に初期化されているか確認
    const tableElement = tableRef.current;
    if (tableElement && typeof window !== 'undefined' && (window as any).jQuery) {
      const $ = (window as any).jQuery;
      if ($.fn.DataTable && !$.fn.DataTable.isDataTable(tableElement)) {
        // DataTables が初期化されていない場合は更新しない
        return;
      }
    }

    try {
      dataTableRef.current.clear();
      dataTableRef.current.rows.add(mapStorageRoomsWithOwnerName(data.data));
      dataTableRef.current.draw();
    } catch (err) {
      logger.error('DataTables更新エラー', toLoggableError(err));
    }
  }, [data?.data, mapStorageRoomsWithOwnerName]);

  const pageHeading = (
    <SampleManagerPageHeading
      shortLabel={t('navigation.storage_room_management_short', { defaultValue: 'Storage' })}
      fullLabel={t('storage_room.title', { defaultValue: 'Storage' })}
      /* ナビ短名「保管」と可視 h1 が縦を奪うため sr-only（一覧／帳票と同型） */
      srOnly
    />
  );

  const renderAdminToggle = (inputId: string, mode: 'dense' | 'compact') =>
    showAdminViewToggle ? (
      <AdminStorageViewToggle
        checked={adminViewAll}
        onChange={setAdminViewAll}
        inputId={inputId}
        dense={mode === 'dense'}
        compact={mode === 'compact'}
        className="flex-shrink-0"
        offShortLabelKey="storage_room.admin_view_scope_mine_short"
      />
    ) : null;

  // 初回のみフルローディング。再取得時はテーブルをアンマウントしない（DataTables SPA 破壊防止）
  if (isLoading && !data) {
    return (
      <Container fluid className="mt-2 px-3" id="storage-rooms-page">
        <SampleManagerNavigation />
        {pageHeading}
        {setupMode ? (
          <Alert variant="info" className="mt-2 mb-2">
            <Alert.Heading as="h2" className="h6 mb-2">
              {t('storage_room.setup_banner_title', {
                defaultValue: 'はじめに · 保管庫の登録',
              })}
            </Alert.Heading>
            <p className="mb-0 small">
              {t('storage_room.setup_banner_desc', {
                defaultValue:
                  'サンプルを登録する前に、保管庫を1つ登録してください。Local Zip・クラウド DB とも同じ手順です。',
              })}
            </p>
          </Alert>
        ) : null}
        <div className="text-center py-5" aria-busy="true">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="mt-2 px-3" id="storage-rooms-page">
        <SampleManagerNavigation />
        {pageHeading}
        <Alert variant="danger" className="mt-2">
          {t('storage_room.load_failed', { defaultValue: 'Failed to load storage rooms.' })}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-2 px-3" id="storage-rooms-page">
      <SampleManagerNavigation />
      {pageHeading}
      {showSetupBanner ? (
        <Alert variant="info" className="mt-2 mb-2">
          <Alert.Heading as="h2" className="h6 mb-2">
            {t('storage_room.setup_banner_title', {
              defaultValue: 'はじめに · 保管庫の登録',
            })}
          </Alert.Heading>
          <p className="mb-2 small">
            {t('storage_room.setup_banner_desc', {
              defaultValue:
                'サンプルを登録する前に、保管庫を1つ登録してください。Local Zip・クラウド DB とも同じ手順です。',
            })}
          </p>
          <ol className="mb-0 small ps-3">
            <li>
              {t('storage_room.setup_step1', {
                defaultValue: 'この画面で保管庫を登録する',
              })}
            </li>
            <li>
              {t('storage_room.setup_step2', {
                defaultValue: '登録後、サンプル一覧でサンプルを追加する',
              })}
            </li>
          </ol>
        </Alert>
      ) : null}
      {showSetupNextStep ? (
        <Alert variant="success" className="mt-2 mb-2" dismissible onClose={() => setShowSetupNextStep(false)}>
          <Alert.Heading as="h2" className="h6 mb-2">
            {t('storage_room.setup_next_title', {
              defaultValue: '保管庫を登録しました。次はサンプルを追加できます。',
            })}
          </Alert.Heading>
          <Button as={Link as any} to="/samples" variant="success" size="sm">
            <i className="fas fa-flask me-1" aria-hidden="true" />
            {t('storage_room.setup_next_cta', {
              defaultValue: 'サンプルを登録する',
            })}
          </Button>
        </Alert>
      ) : null}
      {showAdminViewToggle && isMdUp ? (
        <div className="d-flex justify-content-end mb-2">
          {renderAdminToggle('admin-storage-view-all-rooms', 'dense')}
        </div>
      ) : null}

      {/* 一覧／帳票と同型: 検索 + 操作を1帯（xs は管理者トグルもここへ） */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-1 sample-list-chrome-band storage-rooms-chrome">
        <div className="dataTables-filter-slot flex-grow-1 min-w-0" aria-hidden="true" />
        {/* 1600+: 件数を chrome へ。未満はフッタへ undock（検索幅優先・レポートと同 BP） */}
        <div className="dataTables-length-slot storage-length-chrome flex-shrink-0" aria-hidden="true" />
        <div className="sample-list-chrome-end d-flex align-items-center gap-1 flex-shrink-0">
          {!isMdUp ? renderAdminToggle('admin-storage-view-all-rooms-mobile', 'compact') : null}
          <Button
            variant="primary"
            size="sm"
            className="sample-chrome-icon-btn flex-shrink-0"
            onClick={() => setShowCreateModal(true)}
            title={t('storage_room.new_registration', { defaultValue: 'New registration' })}
            aria-label={t('storage_room.new_registration_short', {
              defaultValue: t('sample.new_registration_short', {
                defaultValue: t('storage_room.new_registration'),
              }),
            })}
          >
            <i className="fas fa-plus" aria-hidden="true" />
          </Button>
          <SampleListChromeTools
            toggleId="storage-rooms-tools-toggle"
            toggleClassName="storage-rooms-tools-toggle"
            compactChrome
            help={
              <CollapsibleHelpSection
                compact
                expandedPortalId="storage-rooms-help-expanded"
                titleKey="storage_room.how_to_use_title"
                descriptionKey="storage_room.how_to_use_desc"
                stepsKey={[
                  'storage_room.how_to_use_step1',
                  'storage_room.how_to_use_step2',
                  'storage_room.how_to_use_step3',
                ]}
                storageKey="storage_rooms_manager"
              />
            }
            menu={
              <>
                <ToolsMenuHelp pageSelector="#storage-rooms-page" />
                {/* カードUIのモバイルでは列トグルの効きが弱いので md+ のみ */}
                <ToolsMenuColVis pageSelector="#storage-rooms-page" />
                <ToolsMenuExport pageSelector="#storage-rooms-page" />
              </>
            }
          />
        </div>
      </div>
      <div id="storage-rooms-help-expanded" />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ページ h1（sr-only「保管」）とパネル見出しが二重になるため置かない */}
      <Card className="border-0 shadow-sm sample-panel-card">
        <Card.Body className="p-0">
          <div id="storage-rooms-table-wrapper" className="table-responsive">
            <table
              ref={tableRef}
              id="storage-rooms-table"
              className="table table-striped table-hover table-bordered mb-0 w-100"
            >
              <thead></thead>
              <tbody></tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      {/* モーダル */}
      <StorageRoomFormModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setError('');
        }}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />

      <StorageRoomFormModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedStorageRoom(null);
          setError('');
        }}
        onSubmit={handleUpdate}
        initialData={selectedStorageRoom}
        isLoading={updateMutation.isPending}
      />

      <StorageRoomDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedStorageRoom(null);
          setError('');
        }}
        onConfirm={handleConfirmDelete}
        storageRoomName={selectedStorageRoom?.name || ''}
        isLoading={deleteMutation.isPending}
      />

      <SampleExternalShareModal
        mode="location"
        show={showLocationShareModal}
        onHide={() => {
          setShowLocationShareModal(false);
          setLocationShareTarget(null);
        }}
        locationTarget={locationShareTarget}
        adminViewAll={adminViewAll}
      />
    </Container>
  );
}

